import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

const TEXT =
    "A great website works while you sleep. It answers questions before they're asked, builds trust before you ever speak, and turns visitors into believers.";

const HIGHLIGHT = new Set(["trust", "believers."]);

const Word = ({ children, index, total, progress }) => {
    const start = index / total;
    const end = start + 1 / total;
    const opacity = useTransform(progress, [start, end], [0.13, 1]);
    const y = useTransform(progress, [start, end], [10, 0]);
    return (
        <motion.span
            style={{ opacity, y }}
            className={`inline-block will-change-transform ${HIGHLIGHT.has(children) ? "text-electric-grad" : ""}`}
        >
            {children}
            {index < total - 1 ? "\u00A0" : ""}
        </motion.span>
    );
};

export const Manifesto = () => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
    const glow = useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0.2]);
    const words = TEXT.split(" ");

    if (reduce) {
        return (
            <section className="relative py-28 md:py-40" data-testid="manifesto-section">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#0066FF] sm:text-sm">Why It Matters</p>
                    <h2 className="mt-8 font-display text-3xl font-extrabold leading-[1.2] text-[#0A0F1D] sm:text-5xl lg:text-6xl">
                        {TEXT}
                    </h2>
                </div>
            </section>
        );
    }

    return (
        <section ref={ref} className="relative h-[260vh]" data-testid="manifesto-section">
            <div className="sticky top-0 flex h-screen items-center overflow-hidden">
                <motion.div
                    style={{ opacity: glow }}
                    className="absolute left-1/2 top-1/2 h-[480px] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0066FF]/10 blur-[130px]"
                    aria-hidden="true"
                />
                <div className="bg-grid-faint absolute inset-0 opacity-40" aria-hidden="true" />
                <div className="relative mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8">
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#0066FF] sm:text-sm">
                        <span className="mr-3 inline-block h-px w-8 translate-y-[-3px] bg-sky-400/70" aria-hidden="true" />
                        Why It Matters
                    </p>
                    <h2 className="mt-8 font-display text-3xl font-extrabold leading-[1.2] tracking-tight text-[#0A0F1D] sm:text-5xl lg:text-[3.6rem]">
                        {words.map((w, i) => (
                            <Word key={`${w}-${i}`} index={i} total={words.length} progress={scrollYProgress}>
                                {w}
                            </Word>
                        ))}
                    </h2>
                </div>
            </div>
        </section>
    );
};
